import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from "@angular/material/dialog";
import { AccountService } from "../../../services/account.service";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { ApiRequestError, GeneralConfirmationModalData, GeneralConfirmationModalResult } from "../../../models";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { DialogService } from "../../../services/dialog.service";
import { DataStorageService } from "../../../services/data-storage.service";
import { ApiHttpService } from "../../../services/api-http.service";

@Component({
  selector: 'cpf-change-iban',
  templateUrl: './change-iban.component.html',
  styleUrls: ['./change-iban.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChangeIbanComponent {
  @Input() showBackButton = true;

  @Output() cancelClicked = new EventEmitter<void>();
  @Output() successClicked = new EventEmitter<void>();

  currentIban = '';
  currentIbanLastFour = '';
  newIban = '';
  errors: string[] = [];

  constructor(private accountService: AccountService,
              private translate: TranslateService,
              private router: Router,
              private dialogService: DialogService,
              private dataStorageService: DataStorageService,
              private apiHttpService: ApiHttpService) {
    this.loadCurrentIban();
  }

  async loadCurrentIban() {
    const user = await this.dataStorageService.user;
    if (user && user.iban) {
      this.currentIban = user.iban;
      // Extract last 4 characters for display
      const cleanIban = user.iban.replace(/\s/g, '');
      this.currentIbanLastFour = cleanIban.slice(-4);
    }
  }

  validate(): boolean {
    this.errors = [];

    if (!this.newIban.trim()) {
      this.errors.push('change-iban.validation.required');
      return false;
    }

    // Basic IBAN format validation
    const cleanIban = this.newIban.replace(/\s/g, '').toUpperCase();

    // Check length (15-34 characters)
    if (cleanIban.length < 15 || cleanIban.length > 34) {
      this.errors.push('change-iban.validation.length');
    }

    // Check format (starts with 2 letters followed by 2 digits)
    if (!/^[A-Z]{2}[0-9]{2}/.test(cleanIban)) {
      this.errors.push('change-iban.validation.format');
    }

    // Check if only alphanumeric characters
    if (!/^[A-Z0-9]+$/.test(cleanIban)) {
      this.errors.push('change-iban.validation.characters');
    }

    return this.errors.length === 0;
  }

  async saveChanges() {
    if (this.validate()) {
      const cleanIban = this.newIban.replace(/\s/g, '').toUpperCase();

      // `/save-user` writes the IBAN to CloudInsurance and mails customer
      // service itself, so `/update-user-mfv` is not called any more. Both
      // calls together produced two emails for one change.
      try {
        await this.apiHttpService.saveUser({
          iban: cleanIban
        });
      } catch (e) {
        console.error(e);

        // A 502 means the insurance system did not take the IBAN. Nothing was
        // saved, so the typed value stays in the field.
        if (e instanceof ApiRequestError && e.isUpstreamFailure) {
          return this.showUpstreamFailModal();
        }

        return this.showFailModal();
      }

      this.currentIban = cleanIban;
      this.currentIbanLastFour = cleanIban.slice(-4);
      this.newIban = '';
      this.dataStorageService.user!.iban = cleanIban;
      return this.showSuccessModal();
    }
  }

  /** Tells the user that the insurance system did not take the new IBAN. */
  showUpstreamFailModal() {
    this.translate.get(['change-iban.modal.upstream-fail.title', 'change-iban.modal.upstream-fail.message']).subscribe(translations => {
      this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['change-iban.modal.upstream-fail.title'],
          message: translations['change-iban.modal.upstream-fail.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm
        } as GeneralConfirmationModalData
      });
    });
  }

  showFailModal() {
    this.translate.get(['change-iban.modal.fail.title', 'change-iban.modal.fail.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['change-iban.modal.fail.title'],
          message: translations['change-iban.modal.fail.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(result => {
        // Handle result if needed
      });
    });
  }

  showSuccessModal() {
    this.translate.get(['change-iban.modal.success.title', 'change-iban.modal.success.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['change-iban.modal.success.title'],
          message: translations['change-iban.modal.success.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (this.successClicked.observed) {
          this.successClicked.emit();
        } else {
          this.router.navigate(['calingo', 'account', 'view']);
        }
      });
    });
  }

  navigateBack() {
    if (this.cancelClicked.observed) {
      this.cancelClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'account', 'view']);
  }

  formatIban(event: any) {
    let value = event.target.value.replace(/\s/g, '').toUpperCase();
    let formattedValue = value.replace(/(.{4})/g, '$1 ').trim();
    this.newIban = formattedValue;
  }
}
